/**
 * SyncStatusPanel - Full Sync Progress Panel
 *
 * Modal panel showing detailed sync progress:
 * - Progress bar with percentage
 * - Session count (pulled / total)
 * - Estimated time remaining
 * - Background/Wait buttons
 *
 * @module components/sync/SyncStatusPanel
 */

import { useEffect, useCallback, useMemo } from "react";
import { useScrollLock } from "~/core/hooks/useScrollLock";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { RefreshCw, Clock, X } from "lucide-react";
import { toast } from "sonner";
import { cn } from "~/core/utils/cn";
import { SyncProgressBar } from "./SyncProgressBar";
import {
  useIsSyncPanelOpen,
  useCloseSyncPanel,
  useMinimizeSyncPanel,
  useSyncPhase,
  useSyncPulled,
  useSyncTotalToPull,
  useSyncProgressPercent,
  useSyncError,
} from "~/core/store/useSyncStatusStore";

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Format number with thousand separators
 */
function formatNumber(num: number): string {
  return num.toLocaleString("ko-KR");
}

/**
 * Estimate remaining time based on progress
 * Returns formatted string like "약 2분"
 */
function estimateRemainingTime(
  pulled: number,
  total: number,
  elapsedMs: number
): string | null {
  if (pulled === 0 || total === 0) return null;

  const rate = pulled / elapsedMs; // sessions per ms
  const remaining = total - pulled;
  const remainingMs = remaining / rate;

  const remainingSeconds = Math.ceil(remainingMs / 1000);
  const remainingMinutes = Math.ceil(remainingSeconds / 60);

  if (remainingMinutes > 1) {
    return `약 ${remainingMinutes}분`;
  }
  if (remainingSeconds > 10) {
    return `약 ${Math.ceil(remainingSeconds / 10) * 10}초`;
  }
  return "거의 완료";
}

// ============================================================================
// COMPONENT
// ============================================================================

export function SyncStatusPanel() {
  const isOpen = useIsSyncPanelOpen();
  const closePanel = useCloseSyncPanel();
  const minimizePanel = useMinimizeSyncPanel();
  const phase = useSyncPhase();
  const pulled = useSyncPulled();
  const totalToPull = useSyncTotalToPull();
  const percent = useSyncProgressPercent();
  const error = useSyncError();
  useScrollLock(isOpen);

  // Track elapsed time for estimation
  const startTime = useMemo(() => Date.now(), []);
  const elapsedMs = Date.now() - startTime;
  const remainingTime = estimateRemainingTime(pulled, totalToPull, elapsedMs);

  // Auto-close panel and show toast when complete
  useEffect(() => {
    if (phase === "complete" && isOpen) {
      const timer = setTimeout(() => {
        closePanel();
        toast.success("데이터 동기화 완료", {
          description: `${formatNumber(pulled)}개 세션 동기화됨`,
        });
      }, 1500);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [phase, isOpen, closePanel, pulled]);

  // Handle ESC key
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        minimizePanel();
      }
    },
    [isOpen, minimizePanel]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  // Don't render on server
  if (typeof window === "undefined") return null;

  const content = (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 z-50 scrim-soft"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={minimizePanel}
          />

          {/* Panel */}
          <motion.div
            className={cn(
              "fixed z-50",
              "left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2",
              "w-[90vw] max-w-md",
              "bg-surface rounded-xl shadow-2xl",
              "border border-line",
              "p-6",
              "form-island"
            )}
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
          >
            {/* Close Button */}
            <button
              onClick={minimizePanel}
              className={cn(
                "absolute top-4 right-4",
                "p-1.5 rounded-lg",
                "text-muted hover:text-main",
                "hover:bg-elevated",
                "transition-colors"
              )}
              aria-label="닫기"
            >
              <X className="w-4 h-4" />
            </button>

            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
              <div
                className={cn(
                  "p-2 rounded-lg",
                  phase === "error" ? "bg-error-fill/10" : "bg-accent/10"
                )}
              >
                <RefreshCw
                  className={cn(
                    "w-5 h-5",
                    phase === "error" ? "text-error-ink" : "text-accent",
                    phase !== "complete" && phase !== "error" && "animate-spin"
                  )}
                />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-main">
                  {phase === "error"
                    ? "동기화 오류"
                    : phase === "complete"
                    ? "동기화 완료"
                    : "데이터 동기화 중..."}
                </h2>
                {error && (
                  <p className="text-sm text-error-ink">{error}</p>
                )}
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mb-4">
              <SyncProgressBar percent={percent} className="h-3" />
            </div>

            {/* Progress Details */}
            <div className="flex items-center justify-between mb-6">
              <span className="text-2xl font-bold text-main tabular-nums">
                {percent}%
              </span>
              <span className="text-sm text-muted">
                {formatNumber(pulled)} / {formatNumber(totalToPull)} 세션
              </span>
            </div>

            {/* Estimated Time */}
            {remainingTime && phase !== "complete" && phase !== "error" && (
              <div className="flex items-center gap-2 mb-6 text-sm text-muted">
                <Clock className="w-4 h-4" />
                <span>예상 남은 시간: {remainingTime}</span>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={minimizePanel}
                className={cn(
                  "flex-1 px-4 py-2.5 rounded-lg",
                  "text-sm font-medium",
                  "bg-elevated hover:bg-emphasis",
                  "text-main",
                  "transition-colors"
                )}
              >
                백그라운드에서 계속
              </button>
              <button
                onClick={closePanel}
                className={cn(
                  "flex-1 px-4 py-2.5 rounded-lg",
                  "text-sm font-medium",
                  "bg-accent hover:bg-accent/90",
                  "text-white",
                  "transition-colors"
                )}
              >
                기다리기
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  return createPortal(content, document.body);
}

export default SyncStatusPanel;
